import React, { useState } from 'react';
import { Sparkles, MapPin, Info, Plus, Pencil, Trash2, Star, ChevronDown, ChevronUp, FileText, Video, ShoppingBag, ExternalLink, CheckCircle2, Clock, Sun, Moon, ArrowUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface ProfileTabProps {
  theme: ZodiacTheme;
  birthData: {
    date: string;
    time: string;
    place: string;
  };
  setBirthData: (data: {
    date: string;
    time: string;
    place: string;
  }) => void;
  setActiveTab: (tab: string) => void;
}
interface AstroProfile {
  id: string;
  name: string;
  relation: string;
  isPrimary: boolean;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  sign: string;
  signIcon: string;
  moon: string;
  ascendant: string;
  element: string;
}
const mockProfiles: AstroProfile[] = [{
  id: 'p1',
  name: 'Я',
  relation: 'Основной',
  isPrimary: true,
  birthDate: '12.04.1990',
  birthTime: '14:30',
  birthPlace: 'Москва, Россия',
  sign: 'Овен',
  signIcon: '♈',
  moon: 'Рак',
  ascendant: 'Лев',
  element: 'Огонь'
}, {
  id: 'p2',
  name: 'Партнёр',
  relation: 'Партнёр',
  isPrimary: false,
  birthDate: '23.08.1992',
  birthTime: '09:15',
  birthPlace: 'Санкт-Петербург, Россия',
  sign: 'Дева',
  signIcon: '♍',
  moon: 'Скорпион',
  ascendant: 'Весы',
  element: 'Земля'
}, {
  id: 'p3',
  name: 'Ребёнок',
  relation: 'Ребёнок',
  isPrimary: false,
  birthDate: '05.01.2020',
  birthTime: '03:45',
  birthPlace: 'Москва, Россия',
  sign: 'Козерог',
  signIcon: '♑',
  moon: 'Телец',
  ascendant: 'Рыбы',
  element: 'Земля'
}];
interface HistoryItem {
  id: string;
  profileId: string;
  type: 'report' | 'consultation' | 'purchase';
  title: string;
  date: string;
  status: 'ready' | 'scheduled' | 'in_progress';
}
const profileHistory: HistoryItem[] = [{
  id: 'h1',
  profileId: 'p1',
  type: 'report',
  title: 'Натальная карта',
  date: '12 Окт 2024',
  status: 'ready'
}, {
  id: 'h2',
  profileId: 'p1',
  type: 'consultation',
  title: 'Синастрия с партнёром',
  date: '20 Окт 2024',
  status: 'scheduled'
}, {
  id: 'h3',
  profileId: 'p1',
  type: 'report',
  title: 'Соляр на 2025',
  date: '05 Окт 2024',
  status: 'ready'
}, {
  id: 'h4',
  profileId: 'p2',
  type: 'report',
  title: 'Натальная карта',
  date: '15 Сен 2024',
  status: 'ready'
}, {
  id: 'h5',
  profileId: 'p2',
  type: 'consultation',
  title: 'Разбор карты',
  date: '01 Окт 2024',
  status: 'in_progress'
}];
const services = [{
  id: 's1',
  icon: '🗺️',
  label: 'Натальная карта',
  desc: 'Полный разбор'
}, {
  id: 's2',
  icon: '☀️',
  label: 'Соляр',
  desc: 'Прогноз на год'
}, {
  id: 's3',
  icon: '💑',
  label: 'Совместимость',
  desc: 'Синастрия'
}, {
  id: 's4',
  icon: '🔮',
  label: 'Транзиты',
  desc: 'Прогноз'
}];
export function ProfileTab({
  theme,
  birthData,
  setBirthData,
  setActiveTab
}: ProfileTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [activeProfileId, setActiveProfileId] = useState('p1');
  const [isEditing, setIsEditing] = useState(false);
  const [showCharacteristic, setShowCharacteristic] = useState(false);
  const [tempData, setTempData] = useState(birthData);
  const activeProfile = mockProfiles.find(p => p.id === activeProfileId) || mockProfiles[0];
  const history = profileHistory.filter(h => h.profileId === activeProfileId);
  const handleSave = () => {
    setBirthData(tempData);
    setIsEditing(false);
    showToast(t('userdash.profile.saved'));
  };
  const getHistoryIcon = (type: string) => {
    if (type === 'report') return <FileText className="w-4 h-4 text-indigo-500" data-id="element-19520" />;
    if (type === 'consultation') return <Video className="w-4 h-4 text-emerald-500" data-id="element-19521" />;
    return <ShoppingBag className="w-4 h-4 text-amber-500" data-id="element-19522" />;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'ready') return <Badge variant="success" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-19523">
          {t('userdash.profile.statusReady')}
        </Badge>;
    if (status === 'scheduled') return <Badge variant="info" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-19524">
          {t('userdash.profile.statusScheduled')}
        </Badge>;
    return <Badge variant="warning" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-19525">
        {t('userdash.profile.statusInProgress')}
      </Badge>;
  };
  return <div className="space-y-5" data-id="element-19526">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-19527">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19528">
          {t('userdash.profile.title')}
        </h2>
      </div>

      {/* Profile Switcher */}
      <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-0.5" data-id="element-19529">
        {mockProfiles.map(p => <button key={p.id} onClick={() => setActiveProfileId(p.id)} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors shrink-0 border ${activeProfileId === p.id ? `${theme.bg} ${theme.text} border-current` : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-19530">
            <span className="text-base" data-id="element-19531">
              {p.id === 'p1' ? theme.icon : p.signIcon}
            </span>
            <span data-id="element-19532">{p.name}</span>
            {p.isPrimary && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" data-id="element-19533" />}
          </button>)}
        <button onClick={() => showToast(t('userdash.profile.addingNew'))} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-slate-500 border border-dashed border-slate-300 hover:border-slate-400 hover:text-slate-700 transition-colors shrink-0" data-id="element-19534">
          <Plus className="w-4 h-4" data-id="element-19535" /> {t('userdash.profile.addProfile')}
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-id="element-19536">
        {/* LEFT: Profile Card + Astro Data */}
        <div className="lg:col-span-1 space-y-4" data-id="element-19537">
          {/* Profile Card */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-19538">
            <div className={`h-20 bg-gradient-to-br ${theme.gradient} flex items-center justify-center relative`} data-id="element-19539">
              <span className="text-4xl text-white drop-shadow-md" data-id="element-19540">
                {activeProfile.id === 'p1' ? theme.icon : activeProfile.signIcon}
              </span>
              {activeProfile.isPrimary && <span className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded-full" data-id="element-19541">
                  {t('userdash.profile.primary')}
                </span>}
            </div>
            <div className="p-4" data-id="element-19542">
              <div className="flex items-center justify-between mb-3" data-id="element-19543">
                <div data-id="element-19544">
                  <h3 className="font-bold text-slate-900" data-id="element-19545">
                    {activeProfile.name}
                  </h3>
                  <span className="text-xs text-slate-500" data-id="element-19546">
                    {activeProfile.relation} · {activeProfile.sign}
                  </span>
                </div>
                <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-19547">
                  {activeProfile.element}
                </Badge>
              </div>

              <div className="space-y-2 text-sm bg-slate-50 rounded-lg p-3 mb-3" data-id="element-19548">
                <div className="flex justify-between" data-id="element-19549">
                  <span className="text-slate-500 text-xs" data-id="element-19550">
                    {t('userdash.profile.date')}
                  </span>
                  <span className="font-medium text-slate-900 text-xs" data-id="element-19551">
                    {activeProfile.birthDate}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-19552">
                  <span className="text-slate-500 text-xs" data-id="element-19553">
                    {t('userdash.profile.time')}
                  </span>
                  <span className="font-medium text-slate-900 text-xs" data-id="element-19554">
                    {activeProfile.birthTime}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-19555">
                  <span className="text-slate-500 text-xs" data-id="element-19556">
                    {t('userdash.profile.place')}
                  </span>
                  <span className="font-medium text-slate-900 text-xs truncate max-w-[140px]" data-id="element-19557">
                    {activeProfile.birthPlace}
                  </span>
                </div>
              </div>

              <div className="flex gap-2" data-id="element-19558">
                <Button variant="primary" size="sm" className="flex-1 text-xs" onClick={() => showToast(t('userdash.profile.selectedForOrder', {
                name: activeProfile.name
              }))} data-id="element-19559">
                  {t('userdash.profile.use')}
                </Button>
                <Button variant="outline" size="sm" className="px-2.5" onClick={() => {
                setIsEditing(true);
                setTempData(birthData);
              }} title="Редактировать" data-id="element-19560">
                  <Pencil className="w-3.5 h-3.5" data-id="element-19561" />
                </Button>
                {!activeProfile.isPrimary && <Button variant="outline" size="sm" className="px-2.5 text-slate-400 hover:text-rose-500" onClick={() => showToast(t('userdash.profile.deleted'))} title="Удалить" data-id="element-19562">
                    <Trash2 className="w-3.5 h-3.5" data-id="element-19563" />
                  </Button>}
              </div>

              {!activeProfile.isPrimary && <button onClick={() => showToast(t('userdash.profile.madePrimary', {
              name: activeProfile.name
            }))} className="w-full mt-2 text-center text-[11px] text-slate-500 hover:text-indigo-600 transition-colors py-1" data-id="element-19564">
                  {t('userdash.profile.makePrimary')}
                </button>}
            </div>
          </div>

          {/* Astro Data */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-id="element-19565">
            <h4 className="font-bold text-slate-900 text-sm" data-id="element-19566">
              {t('userdash.profile.astroData')}
            </h4>
            <div className="space-y-2" data-id="element-19567">
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-id="element-19568">
                <Sun className="w-4 h-4 text-amber-500 shrink-0" data-id="element-19569" />
                <div data-id="element-19570">
                  <div className="text-[10px] text-slate-500" data-id="element-19571">
                    {t('userdash.profile.sun')}
                  </div>
                  <div className="font-semibold text-sm text-slate-900" data-id="element-19572">
                    {activeProfile.sign}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-id="element-19573">
                <Moon className="w-4 h-4 text-blue-400 shrink-0" data-id="element-19574" />
                <div data-id="element-19575">
                  <div className="text-[10px] text-slate-500" data-id="element-19576">
                    {t('userdash.profile.moon')}
                  </div>
                  <div className="font-semibold text-sm text-slate-900" data-id="element-19577">
                    {activeProfile.moon}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg" data-id="element-19578">
                <ArrowUp className="w-4 h-4 text-purple-500 shrink-0" data-id="element-19579" />
                <div data-id="element-19580">
                  <div className="text-[10px] text-slate-500" data-id="element-19581">
                    {t('userdash.profile.ascendant')}
                  </div>
                  <div className="font-semibold text-sm text-slate-900" data-id="element-19582">
                    {activeProfile.ascendant}
                  </div>
                </div>
              </div>
            </div>

            {/* Collapsible characteristic */}
            <button onClick={() => setShowCharacteristic(!showCharacteristic)} className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors pt-1" data-id="element-19583">
              <span data-id="element-19584">{t('userdash.profile.shortChar')}</span>
              {showCharacteristic ? <ChevronUp className="w-3.5 h-3.5" data-id="element-19585" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-19586" />}
            </button>
            {showCharacteristic && <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-lg" data-id="element-19587">
                {t('userdash.profile.charText', {
              sign: activeProfile.sign,
              ascendant: activeProfile.ascendant,
              moon: activeProfile.moon
            })}
              </p>}
          </div>
        </div>

        {/* RIGHT: Edit Form / Services / History */}
        <div className="lg:col-span-2 space-y-5" data-id="element-19588">
          {/* Edit Form */}
          {isEditing && <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4" data-id="element-19589">
              <div className="flex justify-between items-center" data-id="element-19590">
                <h4 className="font-bold text-slate-900 text-sm" data-id="element-19591">
                  {t('userdash.profile.editData')}
                </h4>
                <button onClick={() => setIsEditing(false)} className="text-xs text-slate-500 hover:text-slate-700" data-id="element-19592">
                  {t('userdash.profile.cancel')}
                </button>
              </div>
              <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-xs flex gap-2" data-id="element-19593">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-19594" />
                <span data-id="element-19595">{t('userdash.profile.editHint')}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-19596">
                <Input label={t('userdash.profile.birthDate')} type="date" value={tempData.date} onChange={e => setTempData({
              ...tempData,
              date: e.target.value
            })} data-id="element-19597" />
                <Input label={t('userdash.profile.birthTime')} type="time" value={tempData.time} onChange={e => setTempData({
              ...tempData,
              time: e.target.value
            })} data-id="element-19598" />
              </div>
              <Input label={t('userdash.profile.birthPlace')} placeholder="Москва, Россия" value={tempData.place} onChange={e => setTempData({
            ...tempData,
            place: e.target.value
          })} icon={<MapPin className="w-4 h-4" data-id="element-19600" />} data-id="element-19599" />
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100" data-id="element-19601">
                <Button variant="ghost" size="sm" onClick={() => {
              setTempData(birthData);
              setIsEditing(false);
            }} data-id="element-19602">
                  {t('userdash.profile.cancel')}
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave} data-id="element-19603">
                  {t('userdash.profile.save')}
                </Button>
              </div>
            </div>}

          {/* Services */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-19604">
            <h4 className="font-bold text-slate-900 text-sm mb-3" data-id="element-19605">
              {t('userdash.profile.orderServiceFor', {
              name: activeProfile.name
            })}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-19606">
              {services.map(s => <button key={s.id} onClick={() => showToast(t('userdash.profile.orderingService', {
              service: s.label,
              name: activeProfile.name
            }))} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors text-center group" data-id="element-19607">
                  <span className="text-xl" data-id="element-19608">{s.icon}</span>
                  <span className="text-xs font-medium text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-19609">
                    {s.label}
                  </span>
                  <span className="text-[10px] text-slate-500 hidden sm:block" data-id="element-19610">
                    {s.desc}
                  </span>
                </button>)}
            </div>
          </div>

          {/* History */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-19611">
            <div className="flex justify-between items-center px-4 py-3 border-b border-slate-100" data-id="element-19612">
              <h4 className="font-bold text-slate-900 text-sm" data-id="element-19613">
                {t('userdash.profile.history')}
              </h4>
              <button onClick={() => setActiveTab('orders')} className={`text-xs font-medium ${theme.text} hover:underline`} data-id="element-19614">
                {t('userdash.profile.allOrders')}
              </button>
            </div>
            {history.length > 0 ? <div className="divide-y divide-slate-100" data-id="element-19615">
                {history.map(item => <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/50 transition-colors" data-id="element-19616">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0" data-id="element-19617">
                      {getHistoryIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19618">
                      <div className="font-medium text-sm text-slate-900 truncate" data-id="element-19619">
                        {item.title}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5" data-id="element-19620">
                        <Clock className="w-3 h-3" data-id="element-19621" /> {item.date}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" data-id="element-19622">
                      {getStatusBadge(item.status)}
                      {item.status === 'ready' && <Button variant="ghost" size="sm" className="text-xs px-2 h-7" onClick={() => setActiveTab('reports')} data-id="element-19623">
                          <ExternalLink className="w-3.5 h-3.5" data-id="element-19624" />
                        </Button>}
                    </div>
                  </div>)}
              </div> : <div className="py-8 text-center" data-id="element-19625">
                <FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" data-id="element-19626" />
                <p className="text-xs text-slate-500" data-id="element-19627">
                  {t('userdash.profile.noHistory')}
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}